import { db } from '@/config/firebase';
import { collection, query, where, getDocs, doc, updateDoc, getDoc } from 'firebase/firestore';

interface StudentData {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  address?: string;
  postcode?: string;
  gearbox?: string;
  licenseNumber?: string;
  emergencyContact?: string;
  emergencyPhone?: string;
  medicalConditions?: boolean;
  medicalDetails?: string;
  nervousAboutDriving?: boolean;
  specificGoals?: string;
  focusAreas?: string;
  drivingExperience?: string;
  vehicleType?: string;
  licenseType?: string;
  previousLessons?: string;
  needsUpdate?: boolean;
}

interface UpdateProgress {
  currentStudent: string;
  progress: number;
  totalStudents: number;
  currentOperation: string;
  incompleteStudents?: { id: string; email: string }[];
}

const findUserInfoFromFirestore = async (studentId: string) => {
  const studentInfo: Partial<StudentData> = {};
  
  try {
    // Get user document from Firestore
    const userDoc = await getDoc(doc(db, 'users', studentId));
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      
      // Handle different name formats
      if (userData.displayName) {
        const nameParts = userData.displayName.split(' ');
        studentInfo.firstName = nameParts[0] || '';
        studentInfo.lastName = nameParts.slice(1).join(' ') || '';
      } else if (userData.firstName || userData.lastName) {
        studentInfo.firstName = userData.firstName || '';
        studentInfo.lastName = userData.lastName || '';
      }
      
      // Add other user information
      Object.assign(studentInfo, {
        email: userData.email || '',
        phone: userData.phone || '',
        address: userData.address || '',
        postcode: userData.postcode || '',
        gearbox: userData.gearbox || '',
        licenseNumber: userData.licenseNumber || '',
        emergencyContact: userData.emergencyContact || '',
        emergencyPhone: userData.emergencyPhone || '',
        medicalConditions: userData.medicalConditions || false,
        medicalDetails: userData.medicalDetails || '',
        nervousAboutDriving: userData.nervousAboutDriving || false,
        specificGoals: userData.specificGoals || '',
        focusAreas: userData.focusAreas || '',
        drivingExperience: userData.drivingExperience || '',
        vehicleType: userData.vehicleType || '',
        licenseType: userData.licenseType || '',
        previousLessons: userData.previousLessons || '0'
      });
    }
  } catch (error) {
    console.error(`Error fetching user info for ${studentId}:`, error);
  }
  
  return studentInfo;
};

export const updateAllStudentsData = async (onProgress?: (progress: UpdateProgress) => void) => {
  try {
    // Get all students from users collection
    const usersRef = collection(db, 'users');
    const studentsQuery = query(usersRef, where('role', '==', 'student'));
    const studentsSnapshot = await getDocs(studentsQuery);
    
    const totalStudents = studentsSnapshot.size;
    console.log(`Found ${totalStudents} students to update`);
    
    const incompleteStudents: { id: string; email: string }[] = [];
    
    for (let i = 0; i < studentsSnapshot.docs.length; i++) {
      const studentDoc = studentsSnapshot.docs[i];
      let studentData = {
        id: studentDoc.id,
        ...studentDoc.data()
      } as StudentData;

      // Update progress
      onProgress?.({
        currentStudent: studentData.email || `Student ${i + 1}`,
        progress: Math.round((i / totalStudents) * 100),
        totalStudents,
        currentOperation: 'Processing student data',
        incompleteStudents
      });

      // Get current user information
      const foundInfo = await findUserInfoFromFirestore(studentDoc.id);
      
      // Check if student has incomplete information
      const hasIncompleteInfo = !foundInfo.firstName || !foundInfo.lastName || !foundInfo.phone || !foundInfo.address;
      if (hasIncompleteInfo && studentData.email) {
        incompleteStudents.push({
          id: studentDoc.id,
          email: studentData.email
        });
      }
      
      // Update the user document with found information
      onProgress?.({
        currentStudent: studentData.email || `Student ${i + 1}`,
        progress: Math.round((i / totalStudents) * 100),
        totalStudents,
        currentOperation: 'Updating user profile',
        incompleteStudents
      });

      await updateDoc(doc(db, 'users', studentDoc.id), {
        firstName: foundInfo.firstName || studentData.firstName || '',
        lastName: foundInfo.lastName || studentData.lastName || '',
        phone: foundInfo.phone || studentData.phone || '',
        address: foundInfo.address || studentData.address || '',
        postcode: foundInfo.postcode || studentData.postcode || '',
        gearbox: foundInfo.gearbox || studentData.gearbox || '',
        licenseNumber: foundInfo.licenseNumber || studentData.licenseNumber || '',
        medicalConditions: foundInfo.medicalConditions || studentData.medicalConditions || false,
        medicalDetails: foundInfo.medicalDetails || studentData.medicalDetails || '',
        nervousAboutDriving: foundInfo.nervousAboutDriving || studentData.nervousAboutDriving || false,
        specificGoals: foundInfo.specificGoals || studentData.specificGoals || '',
        focusAreas: foundInfo.focusAreas || studentData.focusAreas || '',
        drivingExperience: foundInfo.drivingExperience || studentData.drivingExperience || '',
        vehicleType: foundInfo.vehicleType || studentData.vehicleType || '',
        licenseType: foundInfo.licenseType || studentData.licenseType || '',
        previousLessons: foundInfo.previousLessons || studentData.previousLessons || '0',
        needsUpdate: hasIncompleteInfo
      });
      
      console.log(`Completed updates for student: ${studentData.email}`);
    }
    
    onProgress?.({
      currentStudent: 'All students',
      progress: 100,
      totalStudents,
      currentOperation: 'Update completed',
      incompleteStudents
    });

    console.log('All student data updates completed successfully');
    if (incompleteStudents.length > 0) {
      console.log('Students needing manual update:', incompleteStudents);
    }
    return true;
  } catch (error) {
    console.error('Error updating student data:', error);
    throw error;
  }
}; 